import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { api } from "../api/apiClient";
import type { AppNotification } from "../api/types";
import { useAuth } from "./AuthContext";

/**
 * In-app notification feed.
 *
 * Transport: since-cursor polling (~25s) plus an immediate sync whenever the
 * tab regains focus/visibility. This is a deliberate choice, not a shortcut:
 * access tokens live in memory only (no cookie the EventSource API could
 * ride on) and couriers use mobile browsers where background sockets are
 * killed anyway — a focus-triggered sync covers exactly the moment a courier
 * looks at their phone. The transport reads the persistent notifications
 * table, so swapping polling for SSE/Web Push later touches only this file.
 */

const POLL_INTERVAL_MS = 25_000;

type NotificationListener = (fresh: AppNotification[]) => void;

interface NotificationsContextValue {
  notifications: AppNotification[];
  unreadCount: number;
  /** Re-sync now (also called automatically by the poll loop). */
  sync: () => Promise<void>;
  markRead: (id: string) => Promise<void>;
  markAllRead: () => Promise<void>;
  /** Subscribe to freshly arrived notifications; returns an unsubscribe fn. */
  subscribe: (listener: NotificationListener) => () => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<AppNotification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);

  const cursorRef = useRef<string | null>(null);
  const listenersRef = useRef<Set<NotificationListener>>(new Set());
  const syncingRef = useRef(false);

  const sync = useCallback(async () => {
    if (syncingRef.current) return;
    syncingRef.current = true;
    try {
      const since = cursorRef.current ?? undefined;
      const [fresh, unread] = await Promise.all([
        api.listNotifications(since ? { since } : {}),
        api.notificationsUnreadCount(),
      ]);
      setUnreadCount(unread.unreadCount);
      if (fresh.length > 0) {
        cursorRef.current = fresh[0].createdAt;
        setNotifications((current) => {
          if (!since) return fresh;
          const known = new Set(current.map((n) => n.id));
          const additions = fresh.filter((n) => !known.has(n.id));
          return [...additions, ...current].slice(0, 50);
        });
        if (since) {
          for (const listener of listenersRef.current) listener(fresh);
        }
      }
    } catch {
      // Polling must never surface transient network errors; the next tick retries.
    } finally {
      syncingRef.current = false;
    }
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      cursorRef.current = null;
      setNotifications([]);
      setUnreadCount(0);
      return;
    }
    void sync();
    const interval = window.setInterval(() => {
      if (document.visibilityState === "visible") void sync();
    }, POLL_INTERVAL_MS);
    const onVisible = () => {
      if (document.visibilityState === "visible") void sync();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", onVisible);
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", onVisible);
    };
  }, [isAuthenticated, sync]);

  const markRead = useCallback(async (id: string) => {
    await api.markNotificationRead(id);
    const now = new Date().toISOString();
    setNotifications((current) =>
      current.map((n) => (n.id === id && !n.readAt ? { ...n, readAt: now } : n)),
    );
    setUnreadCount((count) => Math.max(0, count - 1));
  }, []);

  const markAllRead = useCallback(async () => {
    await api.markAllNotificationsRead();
    const now = new Date().toISOString();
    setNotifications((current) =>
      current.map((n) => (n.readAt ? n : { ...n, readAt: now })),
    );
    setUnreadCount(0);
  }, []);

  const subscribe = useCallback((listener: NotificationListener) => {
    listenersRef.current.add(listener);
    return () => {
      listenersRef.current.delete(listener);
    };
  }, []);

  const value = useMemo(
    () => ({ notifications, unreadCount, sync, markRead, markAllRead, subscribe }),
    [notifications, unreadCount, sync, markRead, markAllRead, subscribe],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx) {
    throw new Error("useNotifications must be used within NotificationsProvider");
  }
  return ctx;
}
